import { Check } from "lucide-react";

const POINTS = [
  {
    title: "Licensed & Fully Insured",
    desc: "WCB coverage, $5M liability. Every crew, every job.",
  },
  {
    title: "Manufacturer-Certified Materials",
    desc: "GAF, IKO, and Malarkey certified installers — full system warranties.",
  },
  {
    title: "Workmanship Warranty",
    desc: "10-year labor warranty in writing. We stand behind the roof, not behind small print.",
  },
  {
    title: "Clean Job Site Guaranteed",
    desc: "Magnetic nail sweeps, daily debris haul, lawn left better than we found it.",
  },
];

export default function WhyCrestline() {
  return (
    <section
      id="why-us"
      data-testid="why-crestline-section"
      className="relative bg-[var(--crest-cream)] text-[#121212] py-24 md:py-36"
    >
      {/* Subtle paper texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Eyebrow */}
        <div className="reveal flex items-center gap-3 mb-10">
          <span className="h-px w-12 bg-[var(--crest-amber)]" />
          <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
            Why Crestline
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left — massive statement */}
          <div className="lg:col-span-7 reveal">
            <h2
              data-testid="why-headline"
              className="font-display uppercase font-extrabold leading-[0.92] tracking-tight text-[#121212]"
              style={{ fontSize: "clamp(2.4rem, 6.2vw, 5.5rem)" }}
            >
              Why Homeowners Choose <span className="text-[var(--crest-amber)]">Crestline.</span>
            </h2>
            <p className="font-body text-base md:text-lg text-[#3a3a3a] mt-8 max-w-xl leading-relaxed font-semibold">
              Most roofs fail because of shortcuts. We don&apos;t take them.
            </p>
            <p className="font-body text-base md:text-lg text-[#3a3a3a] mt-10 max-w-xl leading-relaxed">
              Every project we touch follows the same architectural-grade
              checklist. No subcontracting the hard parts. No &ldquo;good enough.&rdquo;
              Just the standard we&apos;d hold our own homes to.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <div className="font-display font-extrabold text-5xl text-[var(--crest-amber)]">
                15
              </div>
              <div className="font-body text-xs uppercase tracking-[0.3em] text-[#5a5a5a] leading-tight">
                Years
                <br />
                of doing it right
              </div>
            </div>
          </div>

          {/* Right — bullets */}
          <div className="lg:col-span-5 space-y-6">
            {POINTS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`why-point-${i}`}
                className="reveal flex gap-5 pb-6 border-b border-[#121212]/15 last:border-b-0"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#121212] text-[var(--crest-amber)] mt-1">
                  <Check size={18} strokeWidth={3} />
                </div>
                <div>
                  <h3 className="font-display uppercase font-bold text-lg md:text-xl text-[#121212] tracking-tight">
                    {p.title}
                  </h3>
                  <p className="font-body text-sm text-[#3a3a3a] leading-relaxed mt-2">
                    {p.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
